import React, { useState } from 'react';

import { useFormik, FormikProvider } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { parseActionTemplateFormValuesToDTO } from 'config/api/actionLibrary/actionLibrary.parsers';
import type { ActionTemplateCreateDTO } from 'config/api/actionLibrary/actionLibrary.types';
import assetCategoriesApi from 'config/api/assetCategories/assetCategories';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import action_library_messages from 'messages/action_library_messages';
import PATHS from 'router/PATHS';

import ActionTemplateProperties from './_components/ActionTemplateProperties/ActionTemplateProperties';
import ActionTemplateSteps from './_components/ActionTemplateSteps/ActionTemplateSteps';
import { ActionTemplateFormValues } from './ActionTemplateForm.types';
import actionTemplateValidationSchema from './ActionTemplateForm.validation';

type Props = {
  initialValues?: ActionTemplateFormValues;
  isEditMode?: boolean;
  templateId?: number;
};

const ActionTemplateForm: React.FC<Props> = ({ initialValues, isEditMode = false, templateId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const formik = useFormik<ActionTemplateFormValues>({
    enableReinitialize: true,
    initialValues: initialValues || {
      purpose: 'assets',
      area: '',
      subarea: '',
      title: '',
      shortDescription: '',
      criticality: 'medium',
      tags: [],
      referenceRegulation: [],
      reportedTo: '',
      assetTypes: [],
      checklist: [],
      validityPeriod: '',
      validityPeriodDoesNotApply: false,
    },
    validationSchema: actionTemplateValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      // Mark checklist as touched to show validation error if empty
      formik.setFieldTouched('checklist', true);

      try {
        setLoading(true);

        const dto = parseActionTemplateFormValuesToDTO(values);

        if (!isEditMode) {
          dto.status = 'inactive';
        }

        if (isEditMode && templateId) {
          await actionTemplatesApi.updateActionTemplate(templateId, dto);
          await queryClient.invalidateQueries(['actionTemplate', templateId.toString()]);
          enqueueSnackbar(t(action_library_messages.success.updated), { variant: 'success' });
          history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', templateId.toString()));
        } else {
          const { data } = await actionTemplatesApi.createActionTemplate(dto as ActionTemplateCreateDTO);
          enqueueSnackbar(t(action_library_messages.success.created), { variant: 'success' });
          history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', data.id.toString()));
        }
      } catch (error) {
        enqueueSnackbar(isEditMode ? 'Failed to update action template' : 'Failed to create action template', { variant: 'error' });
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const { handleSubmit } = formik;

  // Fetch choices and areas from API
  const { data: choicesData } = useQuery('actionTemplatesChoices', () => actionTemplatesApi.getChoices(), {
    staleTime: 1000 * 60 * 10,
  });

  // Fetch areas with subareas from API
  const { data: areasData } = useQuery('actionTemplatesAreas', () => actionTemplatesApi.getAreas(), {
    staleTime: 1000 * 60 * 10,
  });

  // Fetch asset types
  const { data: assetTypes } = useQuery(QUERY_KEYS.ASSETS_CATEGORIES, assetCategoriesApi.getAllAssetCategoriesForSelect, {
    staleTime: 1000 * 60 * 10,
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <ActionTemplateProperties areasData={areasData} assetTypes={assetTypes} choicesData={choicesData} formik={formik} />
        <ActionTemplateSteps formik={formik} loading={loading} />
      </form>
    </FormikProvider>
  );
};

export default ActionTemplateForm;
